import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { blogService, realizationsService, categoriesService } from '../services/api';
import authService from '../services/auth';
import axios from 'axios';

const REACT_APP_API_URL = 'https://decko.fr/api';

// Styles
const AdminContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  border-bottom: 1px solid ${props => props.theme.colors.gray};
  padding-bottom: 15px;
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  margin: 0;
`;

const AdminNavigation = styled.nav`
  margin-bottom: 30px;
  display: flex;
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.small};
  overflow: hidden;
`;

const NavButton = styled.button`
  background-color: ${props =>
        props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props =>
        props.active ? props.theme.colors.light : props.theme.colors.dark};
  border: none;
  padding: 12px 20px;
  font-size: 1rem;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  flex-grow: 1;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props =>
        props.active ? props.theme.colors.primary : props.theme.colors.gray};
  }
`;

const AdminSection = styled.section`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.medium};
  padding: 25px;
  margin-bottom: 30px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: ${props => props.theme.colors.primary};
`;

const Button = styled.button`
  background-color: ${props => props.theme.colors[props.variant || 'primary']};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  padding: 10px 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    background-color: ${props => props.theme.colors.gray};
    cursor: not-allowed;
  }
`;

const LinkButton = styled(Button)`
  text-decoration: none;
  font-size: 0.9rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 15px;
  background-color: ${props => props.theme.colors.gray};
  border-bottom: 2px solid ${props => props.theme.colors.primary};
`;

const Td = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid ${props => props.theme.colors.gray};
  vertical-align: middle;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const TabContent = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.gray};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: 1rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.gray};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: 1rem;
  min-height: 200px;
  resize: vertical;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.gray};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: 1rem;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.danger};
  margin-top: 5px;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.colors.success};
  margin-top: 5px;
  font-size: 0.9rem;
`;

const UploadContainer = styled.div`
  border: 2px dashed ${props => props.theme.colors.gray};
  border-radius: ${props => props.theme.borderRadius.small};
  padding: 30px;
  text-align: center;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ImagePreview = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 15px;
`;

const PreviewItem = styled.div`
  position: relative;
  width: 120px;
  border-radius: ${props => props.theme.borderRadius.small};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(255, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  font-size: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(255, 0, 0, 0.9);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 25px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${props => props.theme.colors.gray};
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${props => props.theme.colors.primary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => props.theme.colors.dark};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UserName = styled.span`
  font-weight: 500;
`;

const LogoutButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.danger};
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.9rem;
  
  &:hover {
    opacity: 0.8;
  }
`;

// AdminManager Component
const AdminManager = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('blog');
    const [currentUser, setCurrentUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Blog states
    const [blogPosts, setBlogPosts] = useState([]);
    const [blogFormMode, setBlogFormMode] = useState(null);
    const [selectedBlogPost, setSelectedBlogPost] = useState(null);
    const [blogFormData, setBlogFormData] = useState({
        title: '',
        content: '',
        excerpt: '',
        category_id: '',
        is_published: false,
        publish_to_realizations: false,
        featured_image: null,
        images: []
    });
    // Nouveaux états pour stocker les fichiers d'images
    const [featuredImageFile, setFeaturedImageFile] = useState(null);
    const [additionalImageFiles, setAdditionalImageFiles] = useState([]);

    // Realizations states
    const [realizations, setRealizations] = useState([]);
    const [realizationFormMode, setRealizationFormMode] = useState(null);
    const [selectedRealization, setSelectedRealization] = useState(null);
    const [realizationFormData, setRealizationFormData] = useState({
        title: '',
        description: '',
        location: '',
        category_id: '',
        featured_image: null,
        blog_post_id: '',
        images: []
    });
    // Nouveaux états pour les fichiers de réalisations
    const [realizationFeaturedImageFile, setRealizationFeaturedImageFile] = useState(null);
    const [realizationAdditionalImageFiles, setRealizationAdditionalImageFiles] = useState([]);

    // Categories states
    const [categories, setCategories] = useState([]);
    const [categoryFormMode, setCategoryFormMode] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryFormData, setCategoryFormData] = useState({
        name: '',
        description: ''
    });

    // Modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleteType, setDeleteType] = useState(null);

    // Verify authentication
    useEffect(() => {
        const checkAuth = async () => {
            setIsLoading(true);
            try {
                // Vérifier si l'utilisateur est connecté
                var pass = authService.isLoggedIn();
                console.log(pass);
                if (pass != true) {
                    navigate('/login');
                    return;
                }

                setCurrentUser(authService.getCurrentUser());

                // Charger les données initiales
                await Promise.all([
                    loadBlogPosts(),
                    loadRealizations(),
                    loadCategories()
                ]);

                setIsLoading(false);
            } catch (err) {
                console.error('Erreur lors du chargement des données:', err);
                setError('Erreur lors du chargement des données');
                setIsLoading(false);
            }
        };

        checkAuth();
    }, [navigate]);

    // Handle logout
    const handleLogout = () => {
        authService.logout();
        navigate('/login');
    };

    // Load data functions
    const loadBlogPosts = async () => {
        try {
            const response = await blogService.getAll({ published: null });
            setBlogPosts(response.posts || []);
        } catch (err) {
            console.error('Erreur lors du chargement des articles:', err);
            setError('Erreur lors du chargement des articles');
        }
    };

    const loadRealizations = async () => {
        try {
            const response = await realizationsService.getAll();
            setRealizations(response.realizations || []);
        } catch (err) {
            console.error('Erreur lors du chargement des réalisations:', err);
            setError('Erreur lors du chargement des réalisations');
        }
    };

    const loadCategories = async () => {
        try {
            const response = await categoriesService.getAll();
            setCategories(response || []);
        } catch (err) {
            console.error('Erreur lors du chargement des catégories:', err);
            setError('Erreur lors du chargement des catégories');
        }
    };

    // Blog form handlers
    const openAddBlogForm = () => {
        setBlogFormData({
            title: '',
            content: '',
            excerpt: '',
            category_id: '',
            is_published: false,
            publish_to_realizations: false,
            featured_image: null,
            images: []
        });
        setFeaturedImageFile(null);
        setAdditionalImageFiles([]);
        setBlogFormMode('add');
        setSelectedBlogPost(null);
    };

    const openEditBlogForm = (post) => {
        console.log("Post data received:", post);
        console.log("Images received:", post.images);
        const formattedImages = post.images ? post.images.map(image => ({
            ...image,
            // Ajouter l'URL complète pour l'affichage
            url: `https://decko.fr/uploads/${image.filename}`
        })) : [];

        setBlogFormData({
            title: post.title,
            content: post.content,
            excerpt: post.excerpt || '',
            category_id: post.category_id || '',
            is_published: post.is_published,
            publish_to_realizations: post.publish_to_realizations,
            featured_image: post.featured_image || null,
            images: formattedImages
        });
        setFeaturedImageFile(null);
        setAdditionalImageFiles([]);
        setBlogFormMode('edit');
        setSelectedBlogPost(post);
    };

    const closeBlogForm = () => {
        setBlogFormMode(null);
        setSelectedBlogPost(null);
        setFeaturedImageFile(null);
        setAdditionalImageFiles([]);
        setError(null);
        setSuccess(null);
    };

    const handleBlogInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setBlogFormData({
            ...blogFormData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Nouvelle fonction pour gérer la sélection de l'image principale
    const handleBlogImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Stocker le fichier dans le state
            setFeaturedImageFile(file);

            // Créer une URL d'objet pour l'aperçu
            const previewUrl = URL.createObjectURL(file);
            setBlogFormData({
                ...blogFormData,
                featured_image: previewUrl // Pour l'aperçu uniquement
            });
        }
    };

    // Nouvelle fonction pour gérer la sélection des images supplémentaires
    const handleBlogImagesUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            // Stocker les fichiers dans le state
            setAdditionalImageFiles([...additionalImageFiles, ...files]);

            // Créer des objets d'aperçu pour l'interface utilisateur
            const newImages = files.map(file => ({
                id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                filename: file.name,
                alt_text: '',
                url: URL.createObjectURL(file),
                file: file // Référence au fichier pour l'upload ultérieur
            }));

            setBlogFormData({
                ...blogFormData,
                images: [...blogFormData.images, ...newImages]
            });
        }
    };

    const removeImage = (imageId) => {
        // Supprimer l'image de l'état de l'interface utilisateur
        setBlogFormData({
            ...blogFormData,
            images: blogFormData.images.filter(img => img.id !== imageId)
        });

        // Si c'est une image temporaire, la supprimer également de l'état des fichiers
        if (imageId.toString().includes('temp-')) {
            setAdditionalImageFiles(additionalImageFiles.filter(file => {
                // Trouver l'image correspondante
                const image = blogFormData.images.find(img => img.id === imageId);
                return !image || file.name !== image.filename;
            }));
        }
    };

    // Fonction pour uploader une image
    const uploadImage = async (file, type = 'blog') => {
        try {
            // Créer un FormData pour envoyer le fichier
            const formData = new FormData();
            formData.append('image', file);
            formData.append('type', type); // blog ou projects

            // Appeler votre endpoint d'upload d'image
            const response = await axios.post(
                `${REACT_APP_API_URL}/upload`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    withCredentials: true,
                }
            );

            // Retourner le nom du fichier uploadé
            if (response.data && response.data.filename) {
                return response.data.filename;
            }

            return null;
        } catch (error) {
            console.error('Erreur lors de l\'upload de l\'image:', error);
            return null;
        }
    };


    const handleBlogSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setIsLoading(true);

        try {
            // Validation des champs obligatoires
            if (!blogFormData.title.trim()) {
                setError('Le titre est requis');
                setIsLoading(false);
                return;
            }

            if (!blogFormData.content.trim()) {
                setError('Le contenu est requis');
                setIsLoading(false);
                return;
            }

            // Initialiser les données à envoyer
            const postData = {
                title: blogFormData.title,
                content: blogFormData.content,
                excerpt: blogFormData.excerpt || '',
                category_id: blogFormData.category_id || '',
                is_published: blogFormData.is_published,
                publish_to_realizations: blogFormData.publish_to_realizations,
                images: [] // Sera rempli plus tard
            };

            // Upload de l'image principale
            if (featuredImageFile) {
                console.log("Chargement de l'image principale...");
                const formData = new FormData();
                formData.append('image', featuredImageFile);
                // On garde le paramètre type pour compatibilité
                formData.append('type', 'blog');

                try {
                    const uploadResponse = await axios.post(
                        `${REACT_APP_API_URL}/upload`,
                        formData,
                        {
                            headers: { 'Content-Type': 'multipart/form-data' },
                            withCredentials: true,
                        }
                    );

                    if (uploadResponse.data && uploadResponse.data.success && uploadResponse.data.filename) {
                        postData.featured_image = uploadResponse.data.filename;
                        console.log("Image principale uploadée:", uploadResponse.data.filename);
                    } else {
                        throw new Error("Échec de l'upload de l'image principale");
                    }
                } catch (uploadError) {
                    console.error("Erreur lors de l'upload de l'image principale:", uploadError);
                    setError("Erreur lors de l'upload de l'image principale");
                    setIsLoading(false);
                    return;
                }
            } else if (blogFormData.featured_image && typeof blogFormData.featured_image === 'string' &&
                !blogFormData.featured_image.startsWith('blob:')) {
                // Conserver l'image principale existante
                postData.featured_image = blogFormData.featured_image;
            }

            // Préparation des images additionnelles
            // 1. Conserver les images existantes (non temporaires)
            const existingImages = blogFormData.images
                .filter(img => !img.id?.toString().includes('temp-'))
                .map(img => ({
                    id: img.id,
                    filename: img.filename,
                    alt_text: img.alt_text || ''
                }));

            // 2. Upload des nouvelles images
            if (additionalImageFiles.length > 0) {
                console.log(`Upload de ${additionalImageFiles.length} images supplémentaires...`);

                for (const file of additionalImageFiles) {
                    // Vérifier si le fichier existe toujours (n'a pas été supprimé de l'interface)
                    const stillExists = blogFormData.images.some(img =>
                        img.id?.toString().includes('temp-') && img.filename === file.name
                    );

                    if (!stillExists) continue;

                    const formData = new FormData();
                    formData.append('image', file);
                    // On garde le paramètre type pour compatibilité
                    formData.append('type', 'blog');

                    try {
                        const response = await axios.post(
                            `${REACT_APP_API_URL}/upload`,
                            formData,
                            {
                                headers: { 'Content-Type': 'multipart/form-data' },
                                withCredentials: true,
                            }
                        );

                        if (response.data && response.data.success && response.data.filename) {
                            existingImages.push({
                                filename: response.data.filename,
                                alt_text: ''
                            });
                            console.log("Image additionnelle uploadée:", response.data.filename);
                        } else {
                            console.warn("Upload incomplet pour une image:", file.name);
                        }
                    } catch (error) {
                        console.error("Erreur lors de l'upload d'une image:", file.name, error);
                    }
                }
            }

            // Ajouter les images au postData
            postData.images = existingImages;
            console.log("Données finales à envoyer:", postData);
            console.log("Images à enregistrer:", postData.images);

            // Envoi des données au serveur
            let response;
            if (blogFormMode === 'add') {
                console.log("Création d'un nouvel article...");
                response = await blogService.create(postData);
                console.log("Article créé avec succès:", response);
                setSuccess('Article créé avec succès');
            } else if (blogFormMode === 'edit' && selectedBlogPost) {
                console.log("Mise à jour de l'article ID:", selectedBlogPost.id);
                response = await blogService.update(selectedBlogPost.id, postData);
                console.log("Article mis à jour avec succès:", response);
                setSuccess('Article mis à jour avec succès');
            }

            // Recharger la liste des articles
            await loadBlogPosts();

            // Fermer le formulaire après un délai
            setTimeout(() => {
                closeBlogForm();
                setIsLoading(false);
            }, 2000);

        } catch (err) {
            console.error('Erreur lors de la soumission du formulaire:', err);
            setError('Erreur lors de la soumission du formulaire: ' + (err.message || err));
            setIsLoading(false);
        }
    };

    // Realization form handlers
    const openAddRealizationForm = () => {
        setRealizationFormData({
            title: '',
            description: '',
            location: '',
            category_id: '',
            featured_image: null,
            blog_post_id: '',
            images: []
        });
        setRealizationFeaturedImageFile(null);
        setRealizationAdditionalImageFiles([]);
        setRealizationFormMode('add');
        setSelectedRealization(null);
    };

    const openEditRealizationForm = (realization) => {
        // Formater les images pour l'affichage
        const formattedImages = realization.images ? realization.images.map(image => ({
            ...image,
            // Ajouter l'URL complète pour l'affichage avec le même format que les articles
            url: `https://decko.fr/uploads/${image.filename}`
        })) : [];

        setRealizationFormData({
            title: realization.title,
            description: realization.description || '',
            location: realization.location || '',
            category_id: realization.category_id || '',
            featured_image: realization.featured_image || null,
            blog_post_id: realization.blog_post_id || '',
            images: formattedImages
        });
        setRealizationFeaturedImageFile(null);
        setRealizationAdditionalImageFiles([]);
        setRealizationFormMode('edit');
        setSelectedRealization(realization);
    };

    const closeRealizationForm = () => {
        setRealizationFormMode(null);
        setSelectedRealization(null);
        setRealizationFeaturedImageFile(null);
        setRealizationAdditionalImageFiles([]);
        setError(null);
        setSuccess(null);
    };

    const handleRealizationInputChange = (e) => {
        const { name, value } = e.target;
        setRealizationFormData({
            ...realizationFormData,
            [name]: value
        });
    };

    const handleRealizationImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Stocker le fichier
            setRealizationFeaturedImageFile(file);

            // Créer une URL d'objet pour l'aperçu
            const fileURL = URL.createObjectURL(file);
            setRealizationFormData({
                ...realizationFormData,
                featured_image: fileURL // Pour l'aperçu uniquement
            });
        }
    };

    const handleRealizationImagesUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            // Stocker les fichiers
            setRealizationAdditionalImageFiles([...realizationAdditionalImageFiles, ...files]);

            // Créer des objets d'aperçu pour l'interface utilisateur
            const newImages = files.map(file => ({
                id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                filename: file.name,
                alt_text: '',
                url: URL.createObjectURL(file),
                file: file
            }));

            setRealizationFormData({
                ...realizationFormData,
                images: [...realizationFormData.images, ...newImages]
            });
        }
    };

    const removeRealizationImage = (imageId) => {
        // Supprimer l'image de l'état de l'interface utilisateur
        setRealizationFormData({
            ...realizationFormData,
            images: realizationFormData.images.filter(img => img.id !== imageId)
        });

        // Si c'est une image temporaire, la supprimer également de l'état des fichiers
        if (imageId.toString().includes('temp-')) {
            setRealizationAdditionalImageFiles(realizationAdditionalImageFiles.filter(file => {
                // Trouver l'image correspondante
                const image = realizationFormData.images.find(img => img.id === imageId);
                return !image || file.name !== image.filename;
            }));
        }
    };

    const handleRealizationSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setIsLoading(true);

        try {
            // Validate form
            if (!realizationFormData.title.trim()) {
                setError('Le titre est requis');
                setIsLoading(false);
                return;
            }

            // Uploader l'image principale si elle existe
            let featuredImageFilename = realizationFormData.featured_image;
            if (realizationFeaturedImageFile) {
                featuredImageFilename = await uploadImage(realizationFeaturedImageFile, 'projects');
                if (!featuredImageFilename) {
                    setError('Erreur lors de l\'upload de l\'image principale');
                    setIsLoading(false);
                    return;
                }
            }

            // Préparer les images existantes
            const existingImages = realizationFormData.images
                .filter(img => !img.id.toString().includes('temp-'))
                .map(img => ({
                    id: img.id,
                    filename: img.filename,
                    alt_text: img.alt_text || ''
                }));

            // Uploader les nouvelles images
            const newImagesPromises = realizationAdditionalImageFiles.map(file =>
                uploadImage(file, 'projects')
            );
            const newImagesFilenames = await Promise.all(newImagesPromises);

            // Filtrer les uploads réussis et créer des objets d'image
            const newImages = newImagesFilenames
                .filter(filename => filename !== null)
                .map(filename => ({
                    filename: filename,
                    alt_text: ''
                }));

            // Combiner les images existantes et nouvelles
            const allImages = [...existingImages, ...newImages];

            // Prepare form data
            const realizationData = {
                ...realizationFormData,
                featured_image: featuredImageFilename,
                images: allImages
            };

            let response;
            if (realizationFormMode === 'add') {
                response = await realizationsService.create(realizationData);
                setSuccess('Réalisation créée avec succès');
            } else if (realizationFormMode === 'edit' && selectedRealization) {
                response = await realizationsService.update(selectedRealization.id, realizationData);
                setSuccess('Réalisation mise à jour avec succès');
            }

            // Reload realizations
            await loadRealizations();

            // Close form after a delay
            setTimeout(() => {
                closeRealizationForm();
                setIsLoading(false);
            }, 2000);

        } catch (err) {
            console.error('Erreur lors de la soumission:', err);
            setError('Erreur lors de la soumission du formulaire');
            setIsLoading(false);
        }
    };

    // Category form handlers
    const openAddCategoryForm = () => {
        setCategoryFormData({
            name: '',
            description: ''
        });
        setCategoryFormMode('add');
        setSelectedCategory(null);
    };

    const openEditCategoryForm = (category) => {
        setCategoryFormData({
            name: category.name,
            description: category.description || ''
        });
        setCategoryFormMode('edit');
        setSelectedCategory(category);
    };

    const closeCategoryForm = () => {
        setCategoryFormMode(null);
        setSelectedCategory(null);
        setError(null);
        setSuccess(null);
    };

    const handleCategoryInputChange = (e) => {
        const { name, value } = e.target;
        setCategoryFormData({
            ...categoryFormData,
            [name]: value
        });
    };

    const handleCategorySubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        try {
            // Validate form
            if (!categoryFormData.name.trim()) {
                setError('Le nom est requis');
                return;
            }

            let response;
            if (categoryFormMode === 'add') {
                response = await categoriesService.create(categoryFormData);
                setSuccess('Catégorie créée avec succès');
            } else if (categoryFormMode === 'edit' && selectedCategory) {
                response = await categoriesService.update(selectedCategory.id, categoryFormData);
                setSuccess('Catégorie mise à jour avec succès');
            }

            // Reload categories
            await loadCategories();

            // Close form after a delay
            setTimeout(() => {
                closeCategoryForm();
            }, 2000);

        } catch (err) {
            console.error('Erreur lors de la soumission:', err);
            setError('Erreur lors de la soumission du formulaire');
        }
    };

    // Delete handlers
    const confirmDelete = (item, type) => {
        setItemToDelete(item);
        setDeleteType(type);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        try {
            if (deleteType === 'blog') {
                await blogService.delete(itemToDelete.id);
                await loadBlogPosts();
            } else if (deleteType === 'realization') {
                await realizationsService.delete(itemToDelete.id);
                await loadRealizations();
            } else if (deleteType === 'category') {
                await categoriesService.delete(itemToDelete.id);
                await loadCategories();
            }

            setShowDeleteModal(false);
            setSuccess(`${deleteType === 'blog' ? 'Article' : deleteType === 'realization' ? 'Réalisation' : 'Catégorie'} supprimé(e) avec succès`);

            // Clear success message after delay
            setTimeout(() => {
                setSuccess(null);
            }, 3000);

        } catch (err) {
            console.error('Erreur lors de la suppression:', err);
            setError('Erreur lors de la suppression');
            setShowDeleteModal(false);
        }
    };

    if (isLoading) {
        return (
            <AdminContainer>
                <div>Chargement...</div>
            </AdminContainer>
        );
    }

    return (
        <AdminContainer>
            <Header>
                <Title>Administration Decko</Title>
                {currentUser && (
                    <UserInfo>
                        <UserName>Bonjour, {currentUser.username}</UserName>
                        <LogoutButton onClick={handleLogout}>Déconnexion</LogoutButton>
                    </UserInfo>
                )}
            </Header>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <AdminNavigation>
                <NavButton
                    active={activeTab === 'blog'}
                    onClick={() => setActiveTab('blog')}
                >
                    Articles Blog
                </NavButton>
                <NavButton
                    active={activeTab === 'realizations'}
                    onClick={() => setActiveTab('realizations')}
                >
                    Réalisations
                </NavButton>
                <NavButton
                    active={activeTab === 'categories'}
                    onClick={() => setActiveTab('categories')}
                >
                    Catégories
                </NavButton>
            </AdminNavigation>

            {/* Blog Tab */}
            <TabContent active={activeTab === 'blog'}>
                <AdminSection>
                    <SectionHeader>
                        <SectionTitle>Gestion des Articles</SectionTitle>
                        {!blogFormMode && (
                            <Button onClick={openAddBlogForm}>
                                + Nouvel Article
                            </Button>
                        )}
                    </SectionHeader>

                    {blogFormMode ? (
                        <form onSubmit={handleBlogSubmit}>
                            <InputGroup>
                                <Label htmlFor="title">Titre *</Label>
                                <Input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={blogFormData.title}
                                    onChange={handleBlogInputChange}
                                    required
                                />
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="content">Contenu *</Label>
                                <TextArea
                                    id="content"
                                    name="content"
                                    value={blogFormData.content}
                                    onChange={handleBlogInputChange}
                                    required
                                />
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="excerpt">Extrait</Label>
                                <TextArea
                                    id="excerpt"
                                    name="excerpt"
                                    value={blogFormData.excerpt}
                                    onChange={handleBlogInputChange}
                                    rows="3"
                                />
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="category_id">Catégorie</Label>
                                <Select
                                    id="category_id"
                                    name="category_id"
                                    value={blogFormData.category_id}
                                    onChange={handleBlogInputChange}
                                >
                                    <option value="">Sélectionnez une catégorie</option>
                                    {categories.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </Select>
                            </InputGroup>

                            <Checkbox>
                                <input
                                    type="checkbox"
                                    id="is_published"
                                    name="is_published"
                                    checked={blogFormData.is_published}
                                    onChange={handleBlogInputChange}
                                />
                                <Label htmlFor="is_published">Publier l'article</Label>
                            </Checkbox>

                            <Checkbox>
                                <input
                                    type="checkbox"
                                    id="publish_to_realizations"
                                    name="publish_to_realizations"
                                    checked={blogFormData.publish_to_realizations}
                                    onChange={handleBlogInputChange}
                                />
                                <Label htmlFor="publish_to_realizations">Publier dans les réalisations</Label>
                            </Checkbox>

                            <InputGroup>
                                <Label htmlFor="featured_image">Image principale</Label>
                                <Input
                                    type="file"
                                    id="featured_image"
                                    name="featured_image"
                                    onChange={handleBlogImageUpload}
                                    accept="image/*"
                                />
                                {blogFormData.featured_image && (
                                    <PreviewItem>
                                        <PreviewImage
                                            src={
                                                // Si c'est une URL d'objet blob (nouvelle image) ou une URL existante
                                                blogFormData.featured_image.startsWith &&
                                                    (blogFormData.featured_image.startsWith('blob:') || blogFormData.featured_image.startsWith('http'))
                                                    ? blogFormData.featured_image
                                                    : `https://decko.fr/uploads/${blogFormData.featured_image}`
                                            }
                                            alt="Image principale"
                                        />
                                        <RemoveButton onClick={() => {
                                            setBlogFormData({ ...blogFormData, featured_image: null });
                                            setFeaturedImageFile(null);
                                        }}>×</RemoveButton>
                                    </PreviewItem>
                                )}
                            </InputGroup>

                            <InputGroup>
                                <Label>Images supplémentaires</Label>
                                <UploadContainer>
                                    <Input
                                        type="file"
                                        id="images"
                                        name="images"
                                        onChange={handleBlogImagesUpload}
                                        multiple
                                        accept="image/*"
                                    />
                                </UploadContainer>
                                {blogFormData.images.length > 0 && (
                                    <ImagePreview>
                                        {blogFormData.images.map((image, index) => (
                                            <PreviewItem key={image.id || index}>
                                                <PreviewImage
                                                    src={image.url || `https://decko.fr/uploads/${image.filename}`}
                                                    alt={image.alt_text || 'Image'}
                                                />
                                                <div style={{ fontSize: '0.8rem', padding: '5px', textAlign: 'center' }}>
                                                    {image.filename ? image.filename.substring(0, 15) + '...' : 'Nouvelle image'}
                                                </div>
                                                <RemoveButton onClick={() => removeImage(image.id)}>×</RemoveButton>
                                            </PreviewItem>
                                        ))}
                                    </ImagePreview>
                                )}
                            </InputGroup>

                            <FormActions>
                                <Button type="button" variant="danger" onClick={closeBlogForm}>
                                    Annuler
                                </Button>
                                <Button type="submit" variant="success" disabled={isLoading}>
                                    {isLoading ? 'Chargement...' : (blogFormMode === 'add' ? 'Créer' : 'Mettre à jour')}
                                </Button>
                            </FormActions>
                        </form>
                    ) : (
                        <>
                            {blogPosts.length > 0 ? (
                                <Table>
                                    <thead>
                                        <tr>
                                            <Th>Titre</Th>
                                            <Th>Catégorie</Th>
                                            <Th>Statut</Th>
                                            <Th>Date</Th>
                                            <Th>Actions</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {blogPosts.map(post => (
                                            <tr key={post.id}>
                                                <Td>{post.title}</Td>
                                                <Td>{post.category_name || '-'}</Td>
                                                <Td>
                                                    {post.is_published ?
                                                        <span style={{ color: 'green' }}>Publié</span> :
                                                        <span style={{ color: 'orange' }}>Brouillon</span>
                                                    }
                                                </Td>
                                                <Td>{new Date(post.created_at).toLocaleDateString()}</Td>
                                                <Td>
                                                    <ActionButtons>
                                                        <Button
                                                            variant="primary"
                                                            onClick={() => openEditBlogForm(post)}
                                                        >
                                                            Modifier
                                                        </Button>
                                                        <Button
                                                            variant="danger"
                                                            onClick={() => confirmDelete(post, 'blog')}
                                                        >
                                                            Supprimer
                                                        </Button>
                                                    </ActionButtons>
                                                </Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p>Aucun article trouvé. Créez votre premier article !</p>
                            )}
                        </>
                    )}
                </AdminSection>
            </TabContent>

            {/* Realizations Tab */}
            <TabContent active={activeTab === 'realizations'}>
                <AdminSection>
                    <SectionHeader>
                        <SectionTitle>Gestion des Réalisations</SectionTitle>
                        {!realizationFormMode && (
                            <Button onClick={openAddRealizationForm}>
                                + Nouvelle Réalisation
                            </Button>
                        )}
                    </SectionHeader>

                    {realizationFormMode ? (
                        <form onSubmit={handleRealizationSubmit}>
                            <InputGroup>
                                <Label htmlFor="realization_title">Titre *</Label>
                                <Input
                                    type="text"
                                    id="realization_title"
                                    name="title"
                                    value={realizationFormData.title}
                                    onChange={handleRealizationInputChange}
                                    required
                                />
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="realization_description">Description</Label>
                                <TextArea
                                    id="realization_description"
                                    name="description"
                                    value={realizationFormData.description}
                                    onChange={handleRealizationInputChange}
                                />
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="realization_location">Localisation</Label>
                                <Input
                                    type="text"
                                    id="realization_location"
                                    name="location"
                                    value={realizationFormData.location}
                                    onChange={handleRealizationInputChange}
                                />
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="realization_category_id">Catégorie</Label>
                                <Select
                                    id="realization_category_id"
                                    name="category_id"
                                    value={realizationFormData.category_id}
                                    onChange={handleRealizationInputChange}
                                >
                                    <option value="">Sélectionnez une catégorie</option>
                                    {categories.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </Select>
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="realization_blog_post_id">Article de blog associé</Label>
                                <Select
                                    id="realization_blog_post_id"
                                    name="blog_post_id"
                                    value={realizationFormData.blog_post_id}
                                    onChange={handleRealizationInputChange}
                                >
                                    <option value="">Aucun article associé</option>
                                    {blogPosts.map(post => (
                                        <option key={post.id} value={post.id}>
                                            {post.title}
                                        </option>
                                    ))}
                                </Select>
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="realization_featured_image">Image principale</Label>
                                <Input
                                    type="file"
                                    id="realization_featured_image"
                                    name="featured_image"
                                    onChange={handleRealizationImageUpload}
                                    accept="image/*"
                                />
                                {realizationFormData.featured_image && (
                                    <PreviewItem>
                                        <PreviewImage
                                            src={
                                                realizationFormData.featured_image.startsWith &&
                                                    (realizationFormData.featured_image.startsWith('blob:') || realizationFormData.featured_image.startsWith('http'))
                                                    ? realizationFormData.featured_image
                                                    : `https://decko.fr/uploads/${realizationFormData.featured_image}`
                                            }
                                            alt="Image principale"
                                        />
                                        <RemoveButton onClick={() => {
                                            setRealizationFormData({ ...realizationFormData, featured_image: null });
                                            setRealizationFeaturedImageFile(null);
                                        }}>×</RemoveButton>
                                    </PreviewItem>
                                )}
                            </InputGroup>

                            <InputGroup>
                                <Label>Images du projet</Label>
                                <UploadContainer>
                                    <Input
                                        type="file"
                                        id="realization_images"
                                        name="images"
                                        onChange={handleRealizationImagesUpload}
                                        multiple
                                        accept="image/*"
                                    />
                                </UploadContainer>
                                {realizationFormData.images.length > 0 && (
                                    <ImagePreview>
                                        {realizationFormData.images.map((image, index) => (
                                            <PreviewItem key={image.id || index}>
                                                <PreviewImage
                                                    src={image.url || `https://decko.fr/uploads/${image.filename}`}
                                                    alt={image.alt_text || 'Image'}
                                                />
                                                <div style={{ fontSize: '0.8rem', padding: '5px', textAlign: 'center' }}>
                                                    {image.filename ? image.filename.substring(0, 15) + '...' : 'Nouvelle image'}
                                                </div>
                                                <RemoveButton onClick={() => removeRealizationImage(image.id)}>×</RemoveButton>
                                            </PreviewItem>
                                        ))}
                                    </ImagePreview>
                                )}
                            </InputGroup>

                            <FormActions>
                                <Button type="button" variant="danger" onClick={closeRealizationForm}>
                                    Annuler
                                </Button>
                                <Button type="submit" variant="success" disabled={isLoading}>
                                    {isLoading ? 'Chargement...' : (realizationFormMode === 'add' ? 'Créer' : 'Mettre à jour')}
                                </Button>
                            </FormActions>
                        </form>
                    ) : (
                        <>
                            {realizations.length > 0 ? (
                                <Table>
                                    <thead>
                                        <tr>
                                            <Th>Titre</Th>
                                            <Th>Catégorie</Th>
                                            <Th>Localisation</Th>
                                            <Th>Date</Th>
                                            <Th>Actions</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {realizations.map(realization => (
                                            <tr key={realization.id}>
                                                <Td>{realization.title}</Td>
                                                <Td>{realization.category_name || '-'}</Td>
                                                <Td>{realization.location || '-'}</Td>
                                                <Td>{new Date(realization.created_at).toLocaleDateString()}</Td>
                                                <Td>
                                                    <ActionButtons>
                                                        <Button
                                                            variant="primary"
                                                            onClick={() => openEditRealizationForm(realization)}
                                                        >
                                                            Modifier
                                                        </Button>
                                                        <Button
                                                            variant="danger"
                                                            onClick={() => confirmDelete(realization, 'realization')}
                                                        >
                                                            Supprimer
                                                        </Button>
                                                    </ActionButtons>
                                                </Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p>Aucune réalisation trouvée. Créez votre première réalisation !</p>
                            )}
                        </>
                    )}
                </AdminSection>
            </TabContent>

            {/* Categories Tab */}
            <TabContent active={activeTab === 'categories'}>
                <AdminSection>
                    <SectionHeader>
                        <SectionTitle>Gestion des Catégories</SectionTitle>
                        {!categoryFormMode && (
                            <Button onClick={openAddCategoryForm}>
                                + Nouvelle Catégorie
                            </Button>
                        )}
                    </SectionHeader>

                    {categoryFormMode ? (
                        <form onSubmit={handleCategorySubmit}>
                            <InputGroup>
                                <Label htmlFor="category_name">Nom *</Label>
                                <Input
                                    type="text"
                                    id="category_name"
                                    name="name"
                                    value={categoryFormData.name}
                                    onChange={handleCategoryInputChange}
                                    required
                                />
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="category_description">Description</Label>
                                <TextArea
                                    id="category_description"
                                    name="description"
                                    value={categoryFormData.description}
                                    onChange={handleCategoryInputChange}
                                    rows="4"
                                />
                            </InputGroup>

                            <FormActions>
                                <Button type="button" variant="danger" onClick={closeCategoryForm}>
                                    Annuler
                                </Button>
                                <Button type="submit" variant="success">
                                    {categoryFormMode === 'add' ? 'Créer' : 'Mettre à jour'}
                                </Button>
                            </FormActions>
                        </form>
                    ) : (
                        <>
                            {categories.length > 0 ? (
                                <Table>
                                    <thead>
                                        <tr>
                                            <Th>Nom</Th>
                                            <Th>Slug</Th>
                                            <Th>Description</Th>
                                            <Th>Actions</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.map(category => (
                                            <tr key={category.id}>
                                                <Td>{category.name}</Td>
                                                <Td>{category.slug}</Td>
                                                <Td>{category.description || '-'}</Td>
                                                <Td>
                                                    <ActionButtons>
                                                        <Button
                                                            variant="primary"
                                                            onClick={() => openEditCategoryForm(category)}
                                                        >
                                                            Modifier
                                                        </Button>
                                                        <Button
                                                            variant="danger"
                                                            onClick={() => confirmDelete(category, 'category')}
                                                        >
                                                            Supprimer
                                                        </Button>
                                                    </ActionButtons>
                                                </Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p>Aucune catégorie trouvée. Créez votre première catégorie !</p>
                            )}
                        </>
                    )}
                </AdminSection>
            </TabContent>

            {/* Delete confirmation modal */}
            {showDeleteModal && (
                <ModalOverlay>
                    <ModalContent>
                        <ModalHeader>
                            <ModalTitle>Confirmation de suppression</ModalTitle>
                            <CloseButton onClick={() => setShowDeleteModal(false)}>×</CloseButton>
                        </ModalHeader>
                        <p>
                            Êtes-vous sûr de vouloir supprimer {deleteType === 'blog' ? 'l\'article' : deleteType === 'realization' ? 'la réalisation' : 'la catégorie'}
                            <strong> {itemToDelete?.title || itemToDelete?.name}</strong> ?
                            Cette action est irréversible.
                        </p>
                        <FormActions>
                            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                                Annuler
                            </Button>
                            <Button variant="danger" onClick={handleDelete}>
                                Supprimer
                            </Button>
                        </FormActions>
                    </ModalContent>
                </ModalOverlay>
            )}
        </AdminContainer>
    );
};

export default AdminManager;